import { useLocation } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { HeroCrossfade } from '~/ui-rocco/pages/detail-shared/DetailHero';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { professionalHeroBanners } from '@modules/professional/util/professional-hero-banners';

/**
 * Social-profile hero (mobile) — a full-bleed banner, the height of the
 * product page's gallery pictures, that cross-fades between the
 * professional's banner images (matching the desktop hero carousel), with
 * the name and discipline centred on it in the desktop banner's type and the
 * company logo badge ringed in white centred on its bottom edge. The
 * stats and CTA sit below on white in
 * {@link import('./ProfessionalProfileHeader')}.
 *
 * Reads the shared layout loader and lives in the persistent layout, so it
 * renders once and never re-flashes across tab navigation.
 */
export const ProfessionalProfileHero = () => {
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    if (!layoutData) return null;
    const { initial } = layoutData;
    const discipline = initial.PrimaryCategory?.PrettyTitle;

    const bannerImages = professionalHeroBanners(layoutData.banners)
        .map((banner) => banner.libraryImageFile?.DetailBannerStandard?.Desktop)
        .filter((src): src is string => Boolean(src));

    return (
        <section className="relative w-full bg-ground">
            {/* The height of the product page's gallery pictures (84vw) and
                100 more, for the name to sit in. */}
            <div className="relative h-[calc(84vw+6.25rem)] w-full overflow-hidden bg-gray-200">
                <HeroCrossfade images={bannerImages} />
                {/* A flat fifth of black, so the name reads on a bright
                    photograph. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-black/20"
                />
                {/* The transparent header's white marks sit on this
                    photograph; on a bright sky they vanished. The masthead
                    scrim carries them, as the article and directory heroes
                    carry theirs. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#131313]/45 to-transparent"
                />
                {/* The name and the discipline, centred on the banner in the
                    desktop banner's type: Gestura for the name, the
                    discipline as a rule of open capitals under it. */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                    <h1 className="m-0 font-gestura text-[2.75rem] leading-[1.02] font-normal tracking-[-0.045em] text-[#e5e5e5]">
                        {initial.Title}
                    </h1>
                    {discipline ? (
                        <p className="m-0 mt-3 font-sans text-[0.75rem] font-normal tracking-[0.35em] text-white uppercase">
                            {discipline}
                        </p>
                    ) : null}
                </div>
            </div>
            {initial.CompanyLogo ? (
                <RoccoLink
                    // The profile's own tab base: initial.Link is the public
                    // path, which walks the Business Manager preview out onto
                    // the live site.
                    to={professionalTabBase(initial.URLSegment, pathname)}
                    aria-label={`${initial.Title} logo`}
                    className="absolute -bottom-10 left-1/2 z-10 block size-24 -translate-x-1/2 rounded-full border-[3px] border-white bg-white shadow-[0_2px_10px_rgba(0,0,0,0.12)]"
                >
                    <ProfessionalTileLogo
                        image={{
                            src: initial.CompanyLogo,
                            alt: `${initial.Title} logo`,
                        }}
                        background={initial.LogoBackgroundColor ?? null}
                        className="size-full"
                        contrastBorder
                    />
                </RoccoLink>
            ) : null}
        </section>
    );
};
